import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import {
  // Checkbox,
  Box,
  ClickAwayListener,
  Fade,
  Popper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Loader } from 'components/loader';
import { SalesRoute } from 'config';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AssignUserList from './AssignUserList';
import { JobData, User } from './utils';

interface SkillsCellProps {
  skills: string[];
  onMoreClick: (event: React.MouseEvent<HTMLElement>, skills: string[]) => void;
  popperId?: string;
}

const SkillsCell: React.FC<SkillsCellProps> = ({
  skills,
  onMoreClick,
  popperId,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isMeasuring, setIsMeasuring] = useState(true);

  const allSkillsString = skills.length > 0 ? skills.join(', ') : '-';

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current && containerRef.current) {
        const textElement = textRef.current;
        const containerElement = containerRef.current;
        // Check if text is truncated by comparing scroll width with available width
        // Account for potential "More..." button (approximately 60px)
        const availableWidth = containerElement.clientWidth - 0;
        const isTextTruncated = textElement.scrollWidth > availableWidth;
        setIsTruncated(isTextTruncated);
        setIsMeasuring(false);
      }
    };

    // Use a small delay to ensure DOM is fully rendered
    const timeoutId = setTimeout(checkTruncation, 0);

    // Check on window resize
    window.addEventListener('resize', checkTruncation);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [allSkillsString]);

  if (skills.length === 0) {
    return (
      <Typography
        sx={{
          color: '#000000',
          fontFamily: 'Inter',
          fontSize: '14px',
          fontWeight: 400,
        }}>
        -
      </Typography>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        alignItems: 'center',
        display: 'flex',
        maxWidth: '450px',
        minWidth: 0, // Important for flex truncation
        position: 'relative',
      }}>
      <Typography
        component="span"
        ref={textRef}
        sx={{
          color: '#000000',
          flex: 1,
          fontFamily: 'Inter',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '1.5',
          minWidth: 0, // Important for flex truncation
          overflow: 'hidden',
          textOverflow: 'clip',
          whiteSpace: 'nowrap',
        }}>
        {allSkillsString}
      </Typography>
      {!isMeasuring && isTruncated && (
        <button
          className="more-buttom"
          aria-describedby={popperId}
          type="button"
          onClick={(e) => onMoreClick(e, skills)}>
          More...
        </button>
      )}
    </div>
  );
};

interface JobQueueTableProps {
  data: JobData[];
  selectedRows: string[];
  onRowSelect: (id: string) => void;
  onSelectAll: (selected: boolean) => void;
  loading?: boolean;
  users: User[];
  params?: any;
}

const JobQueueTable: React.FC<JobQueueTableProps> = ({
  data,
  selectedRows,
  onRowSelect,
  onSelectAll,
  loading = false,
  users,
  params,
}) => {
  const { VIEWipriseJOB } = SalesRoute;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [skillData, setSkillData] = useState<any[]>([]);
  // const isAllSelected = data.length > 0 && selectedRows.length === data.length;
  // const isIndeterminate =
  //   selectedRows.length > 0 && selectedRows.length < data.length;

  const navigateTo = (jobId: string) => {
    navigate(`${VIEWipriseJOB}/${jobId ?? '1'}`, {
      state: {
        Params: params,
      },
    });
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>, skills: any[]) => {
    event.preventDefault();
    event.stopPropagation();
    // Show all skills in the popper
    setSkillData(skills);
    setAnchorEl(event.currentTarget);
    setOpen((previousOpen) => !previousOpen);
  };

  const canBeOpen = open && Boolean(anchorEl);
  const id = canBeOpen ? 'spring-popper' : undefined;
  // const getStatusBadgeColor = (color: string) => {
  //   switch (color) {
  //     case 'green':
  //       return '#10b981';
  //     case 'orange':
  //       return '#f59e0b';
  //     case 'red':
  //       return '#ef4444';
  //     default:
  //       return '#6b7280';
  //   }
  // };

  const displayData = data.length > 0 ? data : [];

  return (
    <div className="job-queue-table">
      <Table sx={{ minWidth: 650 }} aria-label="job queue table">
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                '&::after': {
                  backgroundColor: '#e5e7eb',
                  content: '""',
                  height: '24px',
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '2px',
                },
                padding: '12px 16px',
                position: 'relative',
              }}>
              <div style={{ alignItems: 'center', display: 'flex' }}>
                {/* <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={(e) => onSelectAll(e.target.checked)}
                  sx={{
                    marginRight: '8px',
                    padding: '4px',
                  }}
                /> */}
                <Typography
                  sx={{
                    color: '#000000 !important',
                    fontFamily: 'Inter !important',
                    fontSize: '13px',
                    fontWeight: 500,
                  }}>
                  Vendor
                </Typography>
              </div>
            </TableCell>
            <TableCell
              sx={{
                '&::after': {
                  backgroundColor: '#e5e7eb',
                  content: '""',
                  height: '24px',
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '2px',
                },
                padding: '12px 16px',
                position: 'relative',
              }}>
              <Typography
                sx={{
                  color: '#000000 !important',
                  fontFamily: 'Inter !important',
                  fontSize: '13px',
                  fontWeight: 500,
                }}>
                Job Title
              </Typography>
            </TableCell>
            <TableCell
              sx={{
                '&::after': {
                  backgroundColor: '#e5e7eb',
                  content: '""',
                  height: '24px',
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '2px',
                },
                padding: '12px 16px',
                position: 'relative',
              }}>
              <Typography
                sx={{
                  color: '#000000 !important',
                  fontFamily: 'Inter !important',
                  fontSize: '13px',
                  fontWeight: 500,
                }}>
                Top Skills
              </Typography>
            </TableCell>
            <TableCell
              sx={{
                '&::after': {
                  backgroundColor: '#e5e7eb',
                  content: '""',
                  height: '24px',
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '2px',
                },
                padding: '12px 16px',
                position: 'relative',
              }}>
              <Typography
                sx={{
                  color: '#000000 !important',
                  fontFamily: 'Inter !important',
                  fontSize: '13px',
                  fontWeight: 500,
                }}>
                EXP
              </Typography>
            </TableCell>
            <TableCell
              sx={{
                '&::after': {
                  backgroundColor: '#e5e7eb',
                  content: '""',
                  height: '24px',
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '2px',
                },
                padding: '12px 16px',
                position: 'relative',
              }}>
              <Typography
                sx={{
                  color: '#000000 !important',
                  fontFamily: 'Inter !important',
                  fontSize: '13px',
                  fontWeight: 500,
                }}>
                Work Auth
              </Typography>
            </TableCell>
            <TableCell
              sx={{
                '&::after': {
                  backgroundColor: '#e5e7eb',
                  content: '""',
                  height: '24px',
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '2px',
                },
                padding: '12px 16px',
                position: 'relative',
              }}>
              <Typography
                sx={{
                  color: '#000000 !important',
                  fontFamily: 'Inter !important',
                  fontSize: '13px',
                  fontWeight: 500,
                }}>
                Bill
              </Typography>
            </TableCell>
            <TableCell
              sx={{
                '&::after': {
                  backgroundColor: '#e5e7eb',
                  content: '""',
                  height: '24px',
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '2px',
                },
                padding: '12px 16px',
                position: 'relative',
              }}>
              <Typography
                sx={{
                  color: '#000000 !important',
                  fontFamily: 'Inter !important',
                  fontSize: '13px',
                  fontWeight: 500,
                }}>
                Location
              </Typography>
            </TableCell>
            <TableCell
              sx={{
                '&::after': {
                  backgroundColor: '#e5e7eb',
                  content: '""',
                  height: '24px',
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '2px',
                },
                padding: '12px 16px',
                position: 'relative',
              }}>
              <Typography
                sx={{
                  color: '#000000 !important',
                  fontFamily: 'Inter !important',
                  fontSize: '13px',
                  fontWeight: 500,
                }}>
                Work Mode
              </Typography>
            </TableCell>
            <TableCell
              sx={{
                '&::after': {
                  backgroundColor: '#e5e7eb',
                  content: '""',
                  height: '24px',
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '2px',
                },
                padding: '12px 16px',
                position: 'relative',
              }}>
              <Typography
                sx={{
                  color: '#000000 !important',
                  fontFamily: 'Inter !important',
                  fontSize: '13px',
                  fontWeight: 500,
                }}>
                Job Type
              </Typography>
            </TableCell>
            <TableCell
              sx={{
                '&::after': {
                  backgroundColor: '#e5e7eb',
                  content: '""',
                  height: '24px',
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '2px',
                },
                padding: '12px 16px',
                position: 'relative',
              }}>
              <Typography
                sx={{
                  color: '#000000 !important',
                  fontFamily: 'Inter !important',
                  fontSize: '13px',
                  fontWeight: 500,
                }}>
                Status
              </Typography>
            </TableCell>
            <TableCell sx={{ padding: '12px 16px', width: '50px' }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell
                colSpan={11}
                align="center"
                sx={{
                  color: '#241a2e',
                  fontFamily: 'Inter !important',
                  fontSize: '14px',
                  fontWeight: 500,
                  padding: '20px',
                  textAlign: 'center',
                }}>
                <Loader newLoader />
              </TableCell>
            </TableRow>
          ) : displayData.length > 0 ? (
            displayData.map((row) => (
              <TableRow
                key={row.id}
                hover
                component="a"
                href={`${VIEWipriseJOB}/${row.id ?? '1'}${
                  params?.score ? `?score=${String(params.score)}` : ''
                }`}
                sx={{
                  '&:hover': {
                    backgroundColor: row?.overdue ? '#FFF3F3' : '#ffffff',
                  },
                  backgroundColor: row?.overdue ? '#FFF3F3' : '#ffffff',
                  cursor: 'pointer',
                  textDecoration: 'none',
                }}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault();
                  navigateTo(row.id);
                }}>
                <TableCell sx={{ padding: '12px 16px' }}>
                  <div style={{ alignItems: 'center', display: 'flex' }}>
                    {/* <Checkbox
                      checked={selectedRows.includes(row.id)}
                      onChange={() => onRowSelect(row.id)}
                      sx={{
                        marginRight: '8px',
                        padding: '4px',
                      }}
                    /> */}
                    <Typography
                      sx={{
                        color: row?.customer?.isTemporary
                          ? '#EF0003'
                          : '#000000',
                        fontFamily: 'Inter !important',
                        fontSize: '14px',
                        fontWeight: 400,
                        textTransform: 'capitalize',
                      }}>
                      {row?.customer?.name || '-'}
                    </Typography>
                  </div>
                </TableCell>

                <TableCell sx={{ padding: '12px 16px' }}>
                  <Typography
                    sx={{
                      color: '#000000',
                      fontFamily: 'Inter !important',
                      fontSize: '14px',
                      fontWeight: 400,
                    }}>
                    {row?.jobTitle || '-'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '12px 16px' }}>
                  <SkillsCell
                    skills={
                      row?.jobSkills
                        ?.map((skill) => skill?.skill?.name)
                        .filter(Boolean) || []
                    }
                    onMoreClick={handleClick}
                    popperId={id}
                  />
                </TableCell>
                <TableCell sx={{ padding: '12px 16px' }}>
                  <Typography
                    sx={{
                      color: '#000000',
                      fontFamily: 'Inter !important',
                      fontSize: '14px',
                      fontWeight: 400,
                      minWidth: '80px',
                    }}>
                    {(() => {
                      const from = Number(row?.expFrom);
                      const to = Number(row?.expTo);
                      if (from > 0 && to > 0) {
                        return `${from} - ${to}`;
                      } else if (from > 0) {
                        return `${from}`;
                      } else if (to > 0) {
                        return `${to}`;
                      } else {
                        return '-';
                      }
                    })()}
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '12px 16px' }}>
                  <Typography
                    sx={{
                      color: '#000000',
                      fontFamily: 'Inter !important',
                      fontSize: '14px',
                      fontWeight: 400,
                      minWidth: '80px',
                    }}>
                    {row?.jobWorkStatus
                      ?.map((workAuth) => workAuth?.workStatus?.name)
                      ?.filter(Boolean)
                      .join(', ')
                      .trim() || '-'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '12px 16px' }}>
                  <Typography
                    sx={{
                      color: '#000000',
                      fontFamily: 'Inter !important',
                      fontSize: '14px',
                      fontWeight: 400,
                      minWidth: '80px',
                    }}>
                    {(() => {
                      const from = Number(row?.payRangeFrom);
                      const to = Number(row?.payRangeTo);
                      if (from > 0 && to > 0) {
                        return `$${from} - $${to}`;
                      } else if (from > 0) {
                        return `$${from}`;
                      } else if (to > 0) {
                        return `$${to}`;
                      } else {
                        return '-';
                      }
                    })()}
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '12px 16px' }}>
                  <Typography
                    sx={{
                      color: '#000000',
                      fontFamily: 'Inter !important',
                      fontSize: '14px',
                      fontWeight: 400,
                      textTransform: 'capitalize',
                    }}>
                    {(() => {
                      const city = row?.city?.trim();
                      const state = row?.state?.trim();
                      if (city && state) {
                        return `${city}, ${state}`;
                      } else if (city) {
                        return city?.toLocaleLowerCase();
                      } else if (state) {
                        return state?.toLocaleUpperCase();
                      } else {
                        return '-';
                      }
                    })()}
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '12px 16px' }}>
                  <Typography
                    sx={{
                      color: '#000000',
                      fontFamily: 'Inter !important',
                      fontSize: '14px',
                      fontWeight: 400,
                      minWidth: '80px',
                    }}>
                    {row?.workType === 'Remote'
                      ? 'R'
                      : row?.workType === 'Onsite'
                      ? 'O/S'
                      : 'H'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '12px 16px' }}>
                  <Typography
                    sx={{
                      color: '#000000',
                      fontFamily: 'Inter !important',
                      fontSize: '14px',
                      fontWeight: 400,
                      minWidth: '80px',
                    }}>
                    {row?.positionType === 'Contract to Hire'
                      ? 'C2H'
                      : row?.positionType === 'Direct Hire'
                      ? 'DH'
                      : row?.positionType === 'Contract'
                      ? 'C'
                      : '-'}
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: '12px 16px' }}>
                  <Tooltip title={row?.candidateMatchStatus || ''} arrow>
                    <div
                      style={{
                        alignItems: 'center',
                        backgroundColor:
                          row?.candidateMatchStatus === 'submitted'
                            ? 'green'
                            : Number(row?.totalMatchedCandidates) > 0
                            ? 'orange'
                            : row?.candidateMatchStatus === 'inProgress'
                            ? '#3ba0e4ff'
                            : 'red',
                        borderRadius: '12px',
                        color: 'white',
                        display: 'flex',
                        fontFamily: 'Inter',
                        fontSize: '14px',
                        fontWeight: 500,
                        justifyContent: 'center',
                        minWidth: '50px',
                        padding: '5px 8px',
                        textAlign: 'center',
                      }}>
                      {row?.candidateMatchStatus === 'inProgress' ? (
                        <HourglassEmptyIcon
                          sx={{
                            '@keyframes flip': {
                              '0%, 100%': {
                                transform: 'rotate(0deg)',
                              },
                              '50%': {
                                transform: 'rotate(180deg)',
                              },
                            },
                            animation: 'flip 2s ease-in-out infinite',
                            fontSize: '16px',
                          }}
                        />
                      ) : row?.candidateMatchStatus === 'submitted' ? (
                        `${row?.totalCandidatesSubmitted ?? 0}/${
                          row?.totalMatchedCandidates ?? 0
                        }`
                      ) : row?.totalMatchedCandidates > 0 ? (
                        `${row?.totalCandidatesShortlisted ?? 0}/${Number(
                          row?.totalMatchedCandidates,
                        )}`
                      ) : (
                        '0'
                      )}
                    </div>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ padding: '12px 16px' }}>
                  <AssignUserList row={row} users={users ?? []} />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={11} align="center" sx={{ padding: '20px' }}>
                <Typography
                  sx={{
                    color: '#6b7280',
                    fontFamily: 'Inter !important',
                    fontSize: '14px',
                    fontWeight: 400,
                  }}>
                  No jobs found.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {open && (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Popper
            sx={{
              zIndex: 1,
            }}
            id={id}
            open={open}
            anchorEl={anchorEl}
            transition>
            {({ TransitionProps }) => (
              <Fade {...TransitionProps}>
                <Box className={'show-more-box'}>
                  {skillData && skillData.length
                    ? skillData.map((skill: string, index: number) => {
                        return (
                          skill + (index + 1 === skillData.length ? '' : ', ')
                        );
                      })
                    : ''}
                </Box>
              </Fade>
            )}
          </Popper>
        </ClickAwayListener>
      )}
    </div>
  );
};

export default JobQueueTable;
